import { AccordionList, Button, Css, HasIdAndName, StaticField, useComputed } from "@homebound/beam";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createMaterialCostUrl } from "src/RouteUrls";
import { MaterialType } from "src/generated/graphqlTypes";
import { BoundMaterialLeadTimeConfigFields } from "src/routes/admin/components/BoundMaterialLeadTimeConfigFields";
import { BoundMaterialLeadTimeConfigVariantsField } from "src/routes/admin/components/BoundMaterialLeadTimeConfigVariantsField";
import { BoundOptionPricingConfigFields } from "src/routes/admin/components/BoundOptionPricingConfigFields";
import { MaterialAssemblyForm } from "src/routes/libraries/material-catalog/components/material-assembly-form/MaterialAssemblyForm";
import { useSaveMaterialListingMutation } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import { getPriceEstimateText } from "src/routes/libraries/material-catalog/components/materialListingForm";
import { useMaterialListingContext } from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";

export type AdditionalDetailsSectionProps = { markets: HasIdAndName[] };

/**
 * Additional details for the material listing.
 * These are not needed very often, so they are collapsed by default and in a different section from the material details section.
 */
export function AdditionalDetailsSection(props: AdditionalDetailsSectionProps) {
  const { markets } = props;
  const { formState, selectedVariantForm: variant } = useMaterialListingContext();
  const [saveMaterialListing] = useSaveMaterialListingMutation();

  const type = useComputed(() => formState.type.value, [formState]);

  const onDeleteLeadTimeConfig = useCallback(async () => {
    // we can only delete a "config" if it actually exists
    if (formState.materialLeadTimeConfig.id.value) {
      await saveMaterialListing({
        variables: {
          input: {
            id: formState.id.value,
            itemId: formState.itemId.value,
            materialLeadTimeConfig: { delete: true, id: formState.materialLeadTimeConfig.id.value },
          },
        },
      });
    }
  }, [formState, saveMaterialListing]);

  const accordions = [
    {
      title: "Pricing",
      children: (
        <div css={Css.df.fdc.gap2.pt2.$}>
          <BoundOptionPricingConfigFields
            preCutoffDesiredMarginInBasisPoints={formState.optionPricingConfig.preCutoffDesiredMarginInBasisPoints}
            postCutoffDesiredMarginInBasisPoints={formState.optionPricingConfig.postCutoffDesiredMarginInBasisPoints}
            selectionFeeInCents={formState.optionPricingConfig.selectionFeeInCents}
            minimumPriceInCents={formState.optionPricingConfig.minimumPriceInCents}
            reasonNote={formState.optionPricingConfig.reasonNote}
          />
          <VariantCostEstimate />
        </div>
      ),
    },
    {
      title: "Lead Time",
      children: (
        <div css={Css.pt2.$}>
          <BoundMaterialLeadTimeConfigFields
            expectedLeadTimeInDays={formState.materialLeadTimeConfig.expectedLeadTimeInDays}
            maxLeadTimeInDays={formState.materialLeadTimeConfig.maxLeadTimeInDays}
            minLeadTimeInDays={formState.materialLeadTimeConfig.minLeadTimeInDays}
            reasonNote={formState.materialLeadTimeConfig.reasonNote}
            deleteConfig={{ canDelete: !!formState.materialLeadTimeConfig.id.value, onDelete: onDeleteLeadTimeConfig }}
          />
          {/* Per-market overrides; dropping these would be a functional regression from the drawer. */}
          <BoundMaterialLeadTimeConfigVariantsField
            markets={markets}
            materialLeadTimeConfig={formState.materialLeadTimeConfig}
            isReadonly={false}
          />
        </div>
      ),
    },
    ...(canHaveComponents(type) && variant
      ? [{ title: "Assembly", children: <MaterialAssemblyForm variant={variant} /> }]
      : []),
  ];

  return <AccordionList accordions={accordions} />;
}

/**
 * The selected variant's cost, linking out to its cost page.
 *
 * Read-only: it's a rollup of bid item estimates and contract line items, edited on that page. It lives
 * under Pricing rather than in Variant Details.
 */
function VariantCostEstimate() {
  const { selectedVariant, selectedVariantForm } = useMaterialListingContext();
  const navigate = useNavigate();
  const variantId = useComputed(() => selectedVariantForm?.id.value, [selectedVariantForm]);

  // Cost is a rollup against a persisted variant; an unsaved Add Variant row has nothing to link to.
  if (!selectedVariant || !variantId) return null;

  return (
    <StaticField label="Cost">
      <Button
        variant="text"
        label={getPriceEstimateText(selectedVariant, "Add Estimate")}
        onClick={() => navigate(createMaterialCostUrl(selectedVariant.id))}
      />
    </StaticField>
  );
}

function canHaveComponents(type: MaterialType | null | undefined): boolean {
  return type === MaterialType.Placeholder || type === MaterialType.Construction;
}
